/**
 * Getting players to make and share videos (owner feedback 27), kept out of the HUD's own elements: a small "Clip"
 * button on the riding screen once the ride can be filmed (15 s), the "Great jump! Save a clip?" toast after a
 * highlight (shown by the session at a calm moment, at most once a minute), the videos-made counter, and the stat
 * line of the share caption. Both buttons pause the ride and open the video dialog; the ride resumes where it was.
 */
import { statLine } from '../game/cinematic';
import { BIG_JUMP_AIR, type Highlight } from '../game/highlights';
import type { MessageKey } from '../i18n';
import { h, icon } from './dom';
import { formatTime } from './logic/format';
import type { GameUi } from './ui';
import { openVideoDialog, resultTime } from './video';
import './share.css';

const TOAST_MS = 7000;
const COUNT_KEY = 'megamx.videos';

/** Videos made on this device (the start screen shows the count). */
export function videoCount(): number {
  try { const n = Number(localStorage.getItem(COUNT_KEY)); return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0; } catch { return 0; }
}
export function countVideo(): void {
  try { localStorage.setItem(COUNT_KEY, String(videoCount() + 1)); } catch { /* storage blocked */ }
}

/** The camera chip with the count (null before the first video). */
export function videoCountChip(ui: GameUi): HTMLElement | null {
  const n = videoCount();
  if (!n) return null;
  const label = ui.tr('video.made', { n });
  return h('div.star-total.video-count', { attrs: { title: label, 'aria-label': label } }, [icon('camera'), h('span', { text: String(n) })]);
}

/** The caption's stat line: "1:12.4 at MX Zielonka, 2.1 s air" (time: the result, else this ride's best lap). */
export function shareLine(ui: GameUi): string {
  const s = ui.host?.rideStats?.();
  const time = resultTime(ui) ?? (s?.bestLap != null ? formatTime(s.bestLap) : null);
  const air = s && s.air >= BIG_JUMP_AIR ? s.air.toFixed(1) : null; // only air worth a mention
  return statLine([time ? ui.tr('video.caption.time', { time }) : ui.tr('video.caption'), air ? ui.tr('video.caption.air', { air }) : null], ui.tr('video.caption'));
}

/** Pause the ride (if riding) and open the video dialog: the ride is snapshotted and resumes from the pause menu. */
export function filmNow(ui: GameUi): void {
  if (ui.state.screen === 'play') ui.dispatch({ type: 'pause' });
  openVideoDialog(ui);
}

export class ClipUi {
  private readonly layer: HTMLElement;
  private readonly btn: HTMLElement;
  private readonly toast: HTMLElement;
  private readonly text: HTMLElement;
  private timer = 0;

  constructor(private readonly ui: GameUi) {
    this.btn = h('button.btn.clip-btn', {
      attrs: { 'data-nav': '', 'aria-label': ui.tr('video.clipAria'), title: ui.tr('video.clipAria') },
      on: { click: (e: Event) => { (e.currentTarget as HTMLElement).blur(); this.hide(); filmNow(ui); } },
    }, [icon('camera'), h('span.clip-label', { text: ui.tr('video.clip') })]);
    this.text = h('span.clip-text');
    this.toast = h('div.clip-toast', { attrs: { role: 'status', 'aria-live': 'polite' } }, [
      this.text,
      h('button.btn.primary.clip-go', { attrs: { 'data-nav': '' }, on: { click: () => { this.hide(); filmNow(ui); } } }, [icon('camera'), h('span', { text: ui.tr('video.create') })]),
      h('button.icon-btn.clip-x', { attrs: { 'aria-label': ui.tr('clip.dismiss'), title: ui.tr('clip.dismiss') }, on: { click: () => this.hide() } }, [icon('close')]),
    ]);
    this.layer = h('div.mx-ui.clip-layer', {}, [this.btn, this.toast]);
    document.body.append(this.layer);
  }

  /** Every frame: the button shows while riding once the ride can be filmed. */
  frame(canFilm: boolean): void {
    document.body.classList.toggle('mx-can-clip', canFilm);
    if (!canFilm && this.toast.classList.contains('show')) this.hide();
  }

  offer(hl: Highlight): void {
    if (this.ui.state.screen !== 'play' || this.ui.replaying || !this.ui.settings.clipTips) return;
    this.text.textContent = this.ui.tr(`clip.toast.${hl.kind}` as MessageKey);
    this.toast.classList.add('show');
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.hide(), TOAST_MS);
  }

  hide(): void {
    clearTimeout(this.timer);
    this.toast.classList.remove('show');
  }

  /** Language change: the static labels. */
  relabel(): void {
    this.btn.setAttribute('aria-label', this.ui.tr('video.clipAria'));
    this.btn.querySelector('.clip-label')!.textContent = this.ui.tr('video.clip');
    this.toast.querySelector('.clip-go span')!.textContent = this.ui.tr('video.create');
  }
}
